import { useRouter } from 'next/router';
import React, { useContext } from 'react';

import { FatalAttraction as FA } from '@vpr/tracking';

import {
  Button,
  Header,
  Center,
  Link,
  Image,
  Text,
  Paragraph,
  DarkMode,
  LightMode,
  useToken,
  readableColor,
  lighten,
  rem,
} from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';
import { ViewerContext } from '@vimeo/viewer';

import { EmbedShowcaseContext } from './context/useEmbedShowcase';
import { AlbumThemes } from './types';

type Props = {
  name: string;
  userId: number;
  url: string;
  brandColor: string;
  ssoEnabled: boolean;
  customLogo: string | null;
  theme: AlbumThemes;
  domain: string;
  id: number;
  ssoConnectionsCount: number;
};

function BrandedLogin({
  name,
  url,
  brandColor,
  ssoEnabled,
  userId: albumUserId,
  customLogo,
  theme,
  domain,
  id,
  ssoConnectionsCount,
}: Props) {
  const viewer = useContext(ViewerContext);
  const embedShowcaseData = useContext(EmbedShowcaseContext);
  const router = useRouter();
  const vimeoHttpsUrl = viewer && viewer.vimeoHttpsUrl;
  const multipleIds = ssoConnectionsCount > 1;
  const buttonTitle =
    ssoEnabled && !multipleIds ? translate('Log in with SSO') : translate('Log in');
  const albumLookup = url ? url : id;
  // TODO: window reference will break with SSR. Need alternative for prerendering.
  const isOffsite = window.location.hostname === domain;
  const offsitePath = router?.query?.offsite === 'redirect' ? '/offsite_redirect' : '?app=showcase';

  const accentColor = brandColor ?? null;
  const [fillBrandColor] = useToken('colors', ['fill-brand']);
  const ColorModeWrapper = theme === 'dark' ? DarkMode : LightMode;

  const prevPageUrl = isOffsite
    ? encodeURIComponent(`${vimeoHttpsUrl}/showcase/${albumLookup}${offsitePath}`)
    : `${vimeoHttpsUrl}/showcase/${albumLookup}`;

  const handleLogin = async (useSSO = true) => {
    const getToken = () =>
      new Promise<void>((resolve, reject) => {
        const messageListener = (event: { data: { type: string }; origin: string }) => {
          try {
            // Validate the origin of the message
            if (event.origin !== vimeoHttpsUrl) {
              return; // Ignore messages from unexpected origins
            }
            if (event.data.type && event.data.type === 'success') {
              resolve();
            } else {
              reject(new Error('Login failed'));
            }
          } finally {
            window.removeEventListener('message', messageListener);
          }
        };
        window.addEventListener('message', messageListener);
      });

    const loginWindow = window.open(
      `/showcase/${id}/authorize?use_sso=${useSSO}`,
      '',
      'width=800,height=600,menubar=no,scrollbars=no,titlebar=no,toolbar=no',
    );

    try {
      await getToken();
      loginWindow?.close();
      location.reload();
    } catch (error) {
      console.error(error.message);
      loginWindow?.close();
    }
  };

  const onLoginClick = () => {
    const signInUrl =
      ssoEnabled && !multipleIds
        ? `${vimeoHttpsUrl}/auth0/connect?prev_page=${prevPageUrl}&team_id=${albumUserId}`
        : `${vimeoHttpsUrl}/log_in?redirect=${prevPageUrl}`;
    window.location.href = signInUrl;
  };

  const handleAndTrackLoginClick = () => {
    FA.trackClick({
      container: 'showcase',
      component: 'login',
      keyword: ssoEnabled ? 'sso' : 'use_email_password',
    });
    if (embedShowcaseData?.isShowcaseEmbeded) {
      handleLogin(ssoEnabled);
    } else {
      onLoginClick();
    }
  };

  return (
    <ColorModeWrapper>
      <Center
        h="100vh"
        minH={rem(400)}
        w="100%"
        bgColor="background"
        flexFlow="column"
        flex="1"
        p="1rem"
      >
        <Center
          maxWidth={rem(520)}
          paddingX={rem(80)}
          paddingY={rem(60)}
          backgroundColor="fill-surface"
          flexFlow="column"
          borderRadius="xl"
          textAlign="center"
        >
          {customLogo !== null && (
            <Image
              src={customLogo}
              alt={name}
              marginX="auto"
              marginBottom={rem(36)}
              display="block"
              maxWidth={rem(260)}
              width="auto"
              maxHeight={rem(200)}
              height="auto"
            />
          )}

          <Header variant="heading-lg" marginBottom={rem(36)} color="text-primary" size="xl">
            {translate({
              singular: 'Log in to {TITLE}',
              replacements: {
                TITLE: name,
              },
            })}
          </Header>
          <Text variant="body-md" color="text-secondary" marginBottom="200" textAlign="center">
            {translate({
              singular:
                'By logging in to Vimeo, you agree to our {TERMS}Terms of Service{/TERMS} and acknowledge our {PRIVACY}Privacy Policy{/PRIVACY}.',
              replacements: {
                TERMS: (text: string) => (
                  <Link
                    key="terms"
                    href="/terms"
                    target="_blank"
                    variant="inline-primary"
                    fontSize="inherit"
                    color="inherit"
                  >
                    {text}
                  </Link>
                ),
                PRIVACY: (text: string) => (
                  <Link
                    key="privacy"
                    href="/privacy"
                    target="_blank"
                    variant="inline-primary"
                    fontSize="inherit"
                    color="inherit"
                  >
                    {text}
                  </Link>
                ),
              },
            })}
          </Text>
          <Button
            size="lg"
            {...(accentColor && {
              bgColor: accentColor,
              color: readableColor(accentColor),
              _hover: {
                bgColor: lighten(accentColor, 0.1),
              },
              _dark: {
                bgColor: accentColor,
                _hover: {
                  bgColor: lighten(accentColor, 0.1),
                },
              },
            })}
            onClick={handleAndTrackLoginClick}
          >
            {buttonTitle}
          </Button>
          {ssoEnabled && !multipleIds && (
            <Paragraph variant="body-md" marginTop="200" color="text-primary">
              {translate({
                singular: '{A}Log in{/A} with email and password',
                replacements: {
                  A: (children: React.ReactNode) => (
                    <Link
                      key="showcase-login-with-email"
                      href="#"
                      onClick={() => handleLogin(false)}
                      variant="inline-primary"
                      fontSize="inherit"
                      style={{ color: accentColor ?? fillBrandColor }}
                    >
                      {children}
                    </Link>
                  ),
                },
              })}
            </Paragraph>
          )}
        </Center>
      </Center>
    </ColorModeWrapper>
  );
}

export default BrandedLogin;
